import React from "react";
import { Icon } from "../components/Icon.js";
import { useEditor } from "./store.js";
import { usePhone } from "../hooks/useMedia.js";
import { labelFor, hasText, hideable, sizeKey, SIZE, sizeOf, withSize, hide, settingsFor, type Kind } from "./editKinds.js";

/**
 * The row under the preview while something on the video is selected: what it is, its size, the few things people do to it,
 * and Done. On a phone it scrolls sideways with finger-sized buttons; nothing floats over the video any more.
 */
export const EditBar: React.FC = () => {
  const clip = useEditor((s) => s.clip);
  const sel = useEditor((s) => s.editSel) as Kind | null;
  const phone = usePhone();
  const { update, setEditSel, setInspectorTab, requestTextEdit, openGallery, setSelectedOverlay } = useEditor.getState();
  if (!clip || !sel) return null;
  const size = sizeOf(clip, sel);
  const range = SIZE[sizeKey(sel)];
  const bump = (d: 1 | -1) => update((c) => { const cur = sizeOf(c, sel); return cur == null ? c : withSize(c, sel, cur + d * range.step); });
  const settings = () => { const [tab, section] = settingsFor(sel); if (sel.startsWith("overlay:")) setSelectedOverlay(sel.slice(8)); setInspectorTab(tab, section); };
  const setY = (y: number) => update((c) => ({ ...c, style: { ...c.style, yPos: y } }));
  const y = clip.style.yPos;
  const framed = clip.frameZoom !== 1 || clip.cropOffset.dx !== 0 || clip.cropOffset.dy !== 0;
  return (
    <div className={`editbar${phone ? " phone" : ""}`} role="toolbar" aria-label={`${labelFor(sel)} tools`}>
      <span className="editbar-lbl">{labelFor(sel)}</span>
      {size != null ? <div className="editbar-size" role="group" aria-label="Size">
        <button onClick={() => bump(-1)} aria-label="Smaller" title="Smaller"><Icon name="minus" size={16} /></button>
        <span className="num" aria-live="polite">{size}{range.unit}</span>
        <button onClick={() => bump(1)} aria-label="Bigger" title="Bigger"><Icon name="plus" size={16} /></button>
      </div> : null}
      {sel === "captions" ? <>
        <div className="editbar-seg" role="group" aria-label="Height on screen">
          <button className={y < 0.36 ? "on" : ""} onClick={() => setY(0.22)}>Top</button>
          <button className={y >= 0.36 && y < 0.62 ? "on" : ""} onClick={() => setY(0.5)}>Middle</button>
          <button className={y >= 0.62 ? "on" : ""} onClick={() => setY(0.74)}>Low</button>
        </div>
        <button className="editbar-btn" onClick={() => openGallery("captions")}><Icon name="grid" size={15} />Looks</button>
      </> : null}
      {sel === "video" ? <button className="editbar-btn" disabled={!framed} onClick={() => update((c) => ({ ...c, frameZoom: 1, cropOffset: { dx: 0, dy: 0 } }))}><Icon name="refresh" size={15} />Reset</button> : null}
      {hasText(sel) ? <button className="editbar-btn" onClick={requestTextEdit}><Icon name="type" size={15} />Edit text</button> : null}
      <button className="editbar-btn" onClick={settings}><Icon name="settings" size={15} />Settings</button>
      {hideable(sel) ? <button className="editbar-btn" onClick={() => { update((c) => hide(c, sel)); setEditSel(null); }}><Icon name="eye" size={15} />Hide</button> : null}
      <span className="spacer" />
      <button className="editbar-btn done" onClick={() => setEditSel(null)}><Icon name="check" size={15} />Done</button>
    </div>
  );
};
